import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { api } from '../hooks/useAuth';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';

const invoiceItemSchema = z.object({
  description: z.string().min(1, 'validation.required'),
  qty: z.number().min(0, 'validation.positive'),
  unitPrice: z.number().min(0, 'validation.positive'),
  vatRate: z.number().min(0).max(100, 'validation.positive'),
});

const invoiceSchema = z.object({
  companyId: z.string().min(1, 'validation.required'),
  customerId: z.string().min(1, 'validation.required'),
  invoiceDate: z.string().min(1, 'validation.required'),
  invoiceNumber: z.string().optional(),
  currency: z.enum(['EUR', 'USD', 'TRY', 'RUB']),
  dateType: z.enum(['zeitraum', 'lieferdatum']).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  deliveryDate: z.string().optional(),
  purpose: z.string().optional(),
  status: z.enum(['draft', 'sent', 'paid', 'unpaid', 'void']).optional(),
  paymentMethod: z.enum(['bank', 'credit_card', 'invoice']).optional(),
  paidAmount: z.number().min(0).optional(),
  items: z.array(invoiceItemSchema).min(1, 'validation.required'),
  notes: z.string().optional(),
}).refine((data) => {
  if (data.dateType === 'zeitraum') {
    return data.startDate && data.endDate;
  }
  if (data.dateType === 'lieferdatum') {
    return data.deliveryDate;
  }
  return true;
}, {
  message: 'validation.dateFieldsRequired',
  path: ['dateType']
});

const InvoiceNew = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentStep, setCurrentStep] = useState(1);
  const [isGeneratingNumber, setIsGeneratingNumber] = useState(false);

  // Fetch companies and customers
  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await api.get('/companies');
      return response.data;
    },
  });

  const { data: customers } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers');
      return response.data;
    },
  });

  // Fetch settings for defaults
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await api.get('/settings');
      return response.data;
    },
  });

  // Create invoice mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post('/invoices', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('invoices.createdSuccess'),
        timer: 2000,
        showConfirmButton: false
      }).then(() => {
        navigate(`/invoices/${data.invoice._id}`);
      });
    },
    onError: (error) => {
      console.error('Invoice creation error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || t('invoices.creationFailed');
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: errorMessage,
        confirmButtonText: t('common.ok')
      });
    },
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      invoiceDate: dayjs().format('YYYY-MM-DD'),
      invoiceNumber: `0001-${dayjs().year()}`,
      currency: settings?.defaultCurrency || 'EUR',
      dateType: 'lieferdatum',
      status: 'draft',
      paymentMethod: 'invoice',
      paidAmount: 0,
      notes: t('invoices.defaultNote'),
      items: [
        {
          description: '',
          qty: 1,
          unitPrice: 0,
          vatRate: settings?.taxDefaults?.vatRate || 19,
        }
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedItems = watch('items');
  const watchedCurrency = watch('currency');
  const watchedDateType = watch('dateType');
  const watchedStatus = watch('status');
  const watchedCompanyId = watch('companyId');
  const watchedInvoiceDate = watch('invoiceDate');

  // Generate next invoice number
  const generateInvoiceNumber = async () => {
    if (!watchedCompanyId || !watchedInvoiceDate) {
      return;
    }

    setIsGeneratingNumber(true);
    try {
      const response = await api.get(`/invoices/next-number?companyId=${watchedCompanyId}&date=${watchedInvoiceDate}`);
      setValue('invoiceNumber', response.data.nextInvoiceNumber);
    } catch (error) {
      console.error('Error generating invoice number:', error);
      Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: t('invoices.invoiceNumberGenerationFailed'),
        confirmButtonText: t('common.ok')
      });
    } finally {
      setIsGeneratingNumber(false);
    }
  };

  // Auto-generate invoice number when company or date changes
  React.useEffect(() => {
    if (watchedCompanyId && watchedInvoiceDate) {
      generateInvoiceNumber();
    }
  }, [watchedCompanyId, watchedInvoiceDate]);

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let vatTotal = 0;

    watchedItems.forEach(item => {
      const lineTotal = item.qty * item.unitPrice;
      const lineVat = lineTotal * (item.vatRate / 100);
      subtotal += lineTotal;
      vatTotal += lineVat;
    });

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      vatTotal: Math.round(vatTotal * 100) / 100,
      grandTotal: Math.round((subtotal + vatTotal) * 100) / 100,
    };
  };

  const totals = calculateTotals();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: watchedCurrency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const onSubmit = (data) => {
    console.log('Invoice form submitted:', data);
    // Ensure status is set and generate invoice number if not provided
    const invoiceData = {
      ...data,
      status: data.status || 'draft',
      invoiceNumber: data.invoiceNumber || `0001-${dayjs().year()}`
    };
    createMutation.mutate(invoiceData);
  };

  const onError = (errors) => {
    console.log('Invoice form validation errors:', errors);
    // Find first error and show SweetAlert
    const firstError = Object.keys(errors)[0];
    const errorMessage = errors[firstError]?.message || t('common.validationError');
    const firstErrorPath = firstError || 'unknown_field';
    Swal.fire({
      icon: 'error',
      title: t('common.validationError'),
      text: `${firstErrorPath && 'Field "' + firstErrorPath + '": ' || ''}${errorMessage}`,
      confirmButtonText: t('common.ok')
    });
  };

  const nextStep = () => {
    // Validate current step before proceeding
    if (currentStep === 1) {
      // Check if company and customer are selected
      const companyId = watch('companyId');
      const customerId = watch('customerId');
      if (!companyId || !customerId) {
        Swal.fire({
          icon: 'warning',
          title: t('common.validationError'),
          text: t('invoices.selectCompanyCustomer'),
          confirmButtonText: t('common.ok')
        });
        return;
      }
    }
    if (currentStep === 2) {
      // Check if invoice date is set
      const invoiceDate = watch('invoiceDate');
      if (!invoiceDate) {
        Swal.fire({
          icon: 'warning',
          title: t('common.validationError'),
          text: t('invoices.setInvoiceDate'),
          confirmButtonText: t('common.ok')
        });
        return;
      }
    }
    if (currentStep === 3) {
      // Check if at least one item is added
      const items = watch('items');
      if (!items || items.length === 0 || items.every(item => !item.description || item.qty === 0 || item.unitPrice === 0)) {
        Swal.fire({
          icon: 'warning',
          title: t('common.validationError'),
          text: t('invoices.addItemBeforeProceeding'),
          confirmButtonText: t('common.ok')
        });
        return;
      }
    }
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const steps = [
    { number: 1, title: t('invoices.companyCustomer') },
    { number: 2, title: t('invoices.invoiceDetails') },
    { number: 3, title: t('invoices.items') },
    { number: 4, title: t('invoices.review') },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('invoices.createInvoice')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('invoices.createNewInvoiceDesc')}
        </p>
      </div>

      {/* Progress Steps */}
      <div className="card">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.number} className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                currentStep >= step.number
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {step.number}
              </div>
              <span className={`ml-2 text-sm font-medium ${
                currentStep >= step.number ? 'text-primary-600' : 'text-gray-500'
              }`}>
                {step.title}
              </span>
              {index < steps.length - 1 && (
                <div className={`w-16 h-0.5 mx-4 ${
                  currentStep > step.number ? 'bg-primary-600' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onError)} className="space-y-6">
        {/* Step 1: Company & Customer */}
        {currentStep === 1 && (
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('invoices.companyCustomer')}</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="label">{t('invoices.company')}</label>
                <select
                  {...register('companyId')}
                  className={`input ${errors.companyId ? 'input-error' : ''}`}
                >
                  <option value="">{t('invoices.selectCompany')}</option>
                  {companies?.map((company) => (
                    <option key={company._id} value={company._id}>
                      {company.name}
                    </option>
                  ))}
                </select>
                {errors.companyId && (
                  <p className="error-message">{t(errors.companyId.message)}</p>
                )}
              </div>
              <div>
                <label className="label">{t('invoices.customer')}</label>
                <select
                  {...register('customerId')}
                  className={`input ${errors.customerId ? 'input-error' : ''}`}
                >
                  <option value="">{t('invoices.selectCustomer')}</option>
                  {customers?.map((customer) => (
                    <option key={customer._id} value={customer._id}>
                      {customer.name}
                    </option>
                  ))}
                </select>
                {errors.customerId && (
                  <p className="error-message">{t(errors.customerId.message)}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Invoice Details */}
        {currentStep === 2 && (
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('invoices.invoiceDetails')}</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="label">{t('invoices.invoiceDate')}</label>
                <input
                  {...register('invoiceDate')}
                  type="date"
                  className={`input ${errors.invoiceDate ? 'input-error' : ''}`}
                />
                {errors.invoiceDate && (
                  <p className="error-message">{t(errors.invoiceDate.message)}</p>
                )}
              </div>
              <div>
                <label className="label">{t('invoices.invoiceNumber')}</label>
                <div className="flex space-x-2">
                  <input
                    {...register('invoiceNumber')}
                    type="text"
                    className={`input flex-1 ${errors.invoiceNumber ? 'input-error' : ''}`}
                    placeholder={t('invoices.invoiceNumberPlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={generateInvoiceNumber}
                    disabled={!watchedCompanyId || !watchedInvoiceDate || isGeneratingNumber}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {isGeneratingNumber ? t('common.loading') : t('invoices.generateNumber')}
                  </button>
                </div>
                {errors.invoiceNumber && (
                  <p className="error-message">{t(errors.invoiceNumber.message)}</p>
                )}
              </div>
            </div>
            
            {/* Date Selection Type */}
            <div className="mt-4">
              <label className="label">{t('invoices.dateSelectionType')}</label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    {...register('dateType')}
                    type="radio"
                    value="zeitraum"
                    className="mr-2"
                  />
                  {t('invoices.zeitraum')}
                </label>
                <label className="flex items-center">
                  <input
                    {...register('dateType')}
                    type="radio"
                    value="lieferdatum"
                    className="mr-2"
                  />
                  {t('invoices.lieferdatum')}
                </label>
              </div>
            </div>

            {/* Conditional Date Fields */}
            {watchedDateType === 'zeitraum' && (
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="label">{t('invoices.startDate')}</label>
                  <input
                    {...register('startDate')}
                    type="date"
                    className={`input ${errors.startDate ? 'input-error' : ''}`}
                  />
                  {errors.startDate && (
                    <p className="error-message">{t(errors.startDate.message)}</p>
                  )}
                </div>
                <div>
                  <label className="label">{t('invoices.endDate')}</label>
                  <input
                    {...register('endDate')}
                    type="date"
                    className={`input ${errors.endDate ? 'input-error' : ''}`}
                  />
                  {errors.endDate && (
                    <p className="error-message">{t(errors.endDate.message)}</p>
                  )}
                </div>
              </div>
            )}

            {watchedDateType === 'lieferdatum' && (
              <div className="mt-4">
                <div>
                  <label className="label">{t('invoices.deliveryDate')}</label>
                  <input
                    {...register('deliveryDate')}
                    type="date"
                    className={`input ${errors.deliveryDate ? 'input-error' : ''}`}
                  />
                  {errors.deliveryDate && (
                    <p className="error-message">{t(errors.deliveryDate.message)}</p>
                  )}
                </div>
              </div>
            )}

            {/* Purpose */}
            <div className="mt-4">
              <label className="label">{t('invoices.purpose')}</label>
              <input
                {...register('purpose')}
                type="text"
                className="input"
                placeholder={t('invoices.purposePlaceholder')}
              />
            </div>

            <div className="mt-4">
              <label className="label">{t('common.currency')}</label>
              <select {...register('currency')} className="input">
                <option value="EUR">EUR - Euro</option>
                <option value="USD">USD - US Dollar</option>
                <option value="TRY">TRY - Turkish Lira</option>
                <option value="RUB">RUB - Russian Ruble</option>
              </select>
            </div>
          </div>
        )}

        {/* Step 3: Items */}
        {currentStep === 3 && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">{t('invoices.items')}</h2>
              <button
                type="button"
                onClick={() => append({ description: '', qty: 1, unitPrice: 0, vatRate: 19 })}
                className="btn btn-primary btn-sm"
              >
                {t('invoices.addItem')}
              </button>
            </div>
            <div className="space-y-4">
              {fields.map((field, index) => (
                <div key={field.id} className="grid grid-cols-1 gap-4 sm:grid-cols-5 items-end">
                  <div className="sm:col-span-2">
                    <label className="label">{t('invoices.description')}</label>
                    <input
                      {...register(`items.${index}.description`)}
                      className={`input ${errors.items?.[index]?.description ? 'input-error' : ''}`}
                      placeholder={t('invoices.description')}
                    />
                  </div>
                  <div>
                    <label className="label">{t('invoices.quantity')}</label>
                    <input
                      {...register(`items.${index}.qty`, { valueAsNumber: true })}
                      type="number"
                      min="0"
                      step="0.01"
                      className={`input ${errors.items?.[index]?.qty ? 'input-error' : ''}`}
                    />
                  </div>
                  <div>
                    <label className="label">{t('invoices.unitPrice')}</label>
                    <input
                      {...register(`items.${index}.unitPrice`, { valueAsNumber: true })}
                      type="number"
                      min="0"
                      step="0.01"
                      className={`input ${errors.items?.[index]?.unitPrice ? 'input-error' : ''}`}
                    />
                  </div>
                  <div className="flex items-end space-x-2">
                    <div className="flex-1">
                      <label className="label">{t('invoices.vatRate')} (%)</label>
                      <input
                        {...register(`items.${index}.vatRate`, { valueAsNumber: true })}
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        className={`input ${errors.items?.[index]?.vatRate ? 'input-error' : ''}`}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="btn btn-danger btn-sm"
                    >
                      {t('invoices.removeItem')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {errors.items && (
              <p className="error-message mt-2">{t(errors.items.message)}</p>
            )}
          </div>
        )}

        {/* Step 4: Review */}
        {currentStep === 4 && (
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('invoices.reviewInvoice')}</h2>
            <div className="space-y-4">
              <div>
                <label className="label">{t('invoices.notes')}</label>
                <textarea
                  {...register('notes')}
                  className="input"
                  rows={3}
                  placeholder={t('invoices.notes')}
                />
              </div>

              {/* Payment Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('invoices.paymentInfo')}</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="label">{t('invoices.status')}</label>
                    <select {...register('status')} className="input">
                      <option value="draft">{t('invoices.statuses.draft')}</option>
                      <option value="sent">{t('invoices.statuses.sent')}</option>
                      <option value="paid">{t('invoices.statuses.paid')}</option>
                      <option value="unpaid">{t('invoices.statuses.unpaid')}</option>
                      <option value="void">{t('invoices.statuses.void')}</option>
                    </select>
                  </div>
                  {(watchedStatus === 'paid' || watchedStatus === 'unpaid') && (
                    <div>
                      <label className="label">{t('invoices.paymentMethod')}</label>
                      <select {...register('paymentMethod')} className="input">
                        <option value="bank">{t('invoices.paymentMethods.bank')}</option>
                        <option value="credit_card">{t('invoices.paymentMethods.credit_card')}</option>
                        <option value="invoice">{t('invoices.paymentMethods.invoice')}</option>
                      </select>
                    </div>
                  )}
                </div>
                
                {watchedStatus === 'unpaid' && (
                  <div className="mt-4">
                    <label className="label">{t('invoices.paidAmount')}</label>
                    <input
                      {...register('paidAmount', { valueAsNumber: true })}
                      type="number"
                      min="0"
                      step="0.01"
                      className="input"
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>
              
              {/* Totals */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('common.totals')}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>{t('common.subtotal')}:</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('common.vat')}:</span>
                    <span>{formatCurrency(totals.vatTotal)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>{t('common.total')}:</span>
                    <span>{formatCurrency(totals.grandTotal)}</span>
                  </div>
                  {watchedStatus === 'unpaid' && (
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>{t('invoices.paid')}</span>
                      <span>{formatCurrency(watch('paidAmount') || 0)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </form>

      {/* Navigation */}
      <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={prevStep}
                    disabled={currentStep === 1}
                    className="btn btn-secondary"
                  >
                    {t('common.previous')}
                  </button>
        {currentStep < 4 ? (
          <button
            type="button"
            onClick={nextStep}
            className="btn btn-primary"
          >
            {t('common.next')}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              // Trigger form submission manually
              const form = document.querySelector('form');
              if (form) {
                form.requestSubmit();
              }
            }}
            disabled={createMutation.isPending}
            className="btn btn-primary"
          >
            {createMutation.isPending ? t('common.loading') : t('invoices.createInvoice')}
          </button>
        )}
      </div>
    </div>
  );
};

export default InvoiceNew;
